import { useState } from 'react'
import { Phone, MessageSquare, Share2, ChevronDown } from 'lucide-react'
import AnchorLink from './AnchorLink'

const SubSectionHeading = ({ children, id }) => (
  <div className="mb-5" id={id}>
    <div className="group flex items-center gap-2">
      <h3 className="wiki-heading text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
        {children}
      </h3>
      {id && <AnchorLink id={id} />}
    </div>
    <div className="mt-2 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
)

const toolTypes = [
  {
    icon: Phone,
    title: 'Contacts',
    desc: 'Traditional methods of communication for direct contact',
    accent: 'border-green-500',
    iconColor: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/20',
    items: ['Phone', 'Mobile Phone', 'Email', 'Fax', 'Landline', 'and other phone/email types']
  },
  {
    icon: MessageSquare,
    title: 'Messenger',
    desc: 'Instant messaging apps that people use to chat',
    accent: 'border-blue-500',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-50 dark:bg-blue-900/20',
    items: ['WhatsApp', 'Telegram', 'Viber', 'Skype', 'WeChat', 'Signal', 'Discord', 'and other messaging apps']
  },
  {
    icon: Share2,
    title: 'Social network',
    desc: 'Social media profiles and professional networking platforms',
    accent: 'border-purple-500',
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-50 dark:bg-purple-900/20',
    items: ['LinkedIn', 'Facebook', 'Instagram', 'Twitter (X)', 'GitHub', 'Behance', 'and other social networks']
  }
]

const whyReasons = [
  {
    label: '1. It belongs to a different category',
    detail: 'For example, "Website" or "Video Call" are separate types and not included in the contact list'
  },
  {
    label: "2. It's marked as not commonly used",
    detail: 'The system only shows frequently used contact types to keep the list manageable'
  },
  {
    label: "3. It's been disabled",
    detail: 'Some contact types might be temporarily disabled or archived'
  }
]

const quickGuideItems = [
  {
    step: '01',
    icon: Phone,
    iconColor: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-50 dark:bg-green-900/20',
    label: 'Use Contacts',
    desc: 'For phone numbers and emails'
  },
  {
    step: '02',
    icon: MessageSquare,
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-50 dark:bg-blue-900/20',
    label: 'Use Messenger',
    desc: 'For instant messaging apps (WhatsApp, Telegram, etc.)'
  },
  {
    step: '03',
    icon: Share2,
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-50 dark:bg-purple-900/20',
    label: 'Use Social Network',
    desc: 'For social media profiles (LinkedIn, Facebook, etc.)'
  }
]

const ContactToolsSection = () => {
  const [whyOpen, setWhyOpen] = useState(false)

  return (
    <div className="space-y-10">

      {/* Overview callout */}
      <div className="pl-4 border-l-2 border-primary-500/40 bg-primary-50/50 dark:bg-primary-900/10 rounded-r-lg py-3 pr-4">
        <h3 className="wiki-heading text-base font-semibold text-light-text-primary dark:text-dark-text-primary mb-1">
          What is Contact Type?
        </h3>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
          <strong className="text-light-text-primary dark:text-dark-text-primary">Contact Type</strong> is a list of communication tools filtered by three specific categories (tool types):
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {['Contacts', 'Messenger', 'Social Network'].map(tag => (
            <span key={tag} className="wiki-mono text-xs px-2 py-0.5 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary">
              {tag}
            </span>
          ))}
        </div>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
          When you add a contact, you'll see only tools from these three categories.
        </p>
      </div>

      {/* Tool Types */}
      <div>
        <SubSectionHeading id="contact-tools-types">What tools are included in each type?</SubSectionHeading>

        <div className="space-y-4">
          {toolTypes.map(tool => {
            const Icon = tool.icon
            return (
              <div
                key={tool.title}
                className={`flex gap-4 p-5 rounded-xl border border-light-border-primary dark:border-dark-border-primary border-l-4 ${tool.accent} bg-light-bg-primary dark:bg-dark-bg-primary`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tool.iconBg}`}>
                  <Icon className={`w-5 h-5 ${tool.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="wiki-heading text-base font-semibold text-light-text-primary dark:text-dark-text-primary mb-1">
                    {tool.title}
                  </h4>
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-3">
                    {tool.desc}
                  </p>
                  <p className="wiki-mono text-[11px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-2">
                    What you can choose:
                  </p>
                  <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {tool.items.map(item => (
                      <li key={item} className="text-sm text-light-text-secondary dark:text-dark-text-secondary flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-light-text-tertiary dark:bg-dark-text-tertiary flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Discord ID note */}
      <div className="pl-4 border-l-2 border-blue-500/40 bg-blue-50/50 dark:bg-blue-900/10 rounded-r-lg py-3 pr-4">
        <h4 className="wiki-heading text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-1">
          Adding a Discord contact?
        </h4>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
          Use the talent's numeric <strong className="text-light-text-primary dark:text-dark-text-primary">Discord User ID</strong>, not their username — the username can change and isn't enough to message them directly. To copy it: in Discord, enable{' '}
          <span className="wiki-mono text-xs px-1 py-0.5 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
            Settings → Advanced → Developer Mode
          </span>
          , then right-click the user and choose <span className="wiki-mono text-xs px-1 py-0.5 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary">Copy User ID</span>.
        </p>
      </div>

      {/* Why collapsible */}
      <div>
        <div id="contact-tools-why" className="scroll-mt-6 border-t border-light-border-primary dark:border-dark-border-primary group">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                onClick={() => setWhyOpen(v => !v)}
                className="text-sm font-medium text-left text-light-text-primary dark:text-dark-text-primary hover:text-primary-500 transition-colors"
              >
                Why don't I see some contact types?
              </button>
              <AnchorLink id="contact-tools-why" />
            </div>
            <button onClick={() => setWhyOpen(v => !v)} className="flex-shrink-0 ml-4">
              <ChevronDown
                className={[
                  'w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary transition-transform duration-200',
                  whyOpen ? 'rotate-180' : ''
                ].join(' ')}
              />
            </button>
          </div>
          {whyOpen && (
            <div className="pb-4 space-y-3 text-sm text-light-text-secondary dark:text-dark-text-secondary">
              <p>If you're looking for a specific contact type but don't see it in the list, it might be because:</p>
              {whyReasons.map(r => (
                <div key={r.label}>
                  <p className="font-medium text-light-text-primary dark:text-dark-text-primary">{r.label}</p>
                  <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-0.5">{r.detail}</p>
                </div>
              ))}
              <p className="pt-3 border-t border-light-border-primary dark:border-dark-border-primary">
                <strong className="text-light-text-primary dark:text-dark-text-primary">Need a contact type that's not listed?</strong>{' '}
                Contact your system administrator or{' '}
                <a
                  href="https://libs.anyemp.com/tools"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:text-primary-600 underline underline-offset-2"
                >
                  add it to the library
                </a>.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Guide */}
      <div>
        <SubSectionHeading id="contact-tools-guide">Quick Guide</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          When adding a contact:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {quickGuideItems.map(item => {
            const Icon = item.icon
            return (
              <div key={item.step} className="flex flex-col gap-3 p-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
                <div className="flex items-center justify-between">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.iconBg}`}>
                    <Icon className={`w-5 h-5 ${item.iconColor}`} />
                  </div>
                  <span className="wiki-mono text-2xl font-bold text-light-border-primary dark:text-dark-bg-tertiary">
                    {item.step}
                  </span>
                </div>
                <div>
                  <p className="wiki-heading text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">
                    {item.label}
                  </p>
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5">
                    {item.desc}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

    </div>
  )
}

export default ContactToolsSection
